// NoticePage — 공지사항 목록 (BBS 테이블 스타일, 게시판과 같은 템플릿)
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../shared/api/client";
import { PageHeader } from "../../shared/components/PageHeader";
import { Pill } from "../../shared/components/Pill";
import { useAuth } from "../../shared/stores/auth";
import { toast } from "../../shared/stores/toasts";
import { ContextMenu } from "../../shell/SubNav";

// 구분선(섹션) 카테고리는 라벨이 ❖ 로 시작 — 탭에는 노출하지 않음
const isDivider = (label: string) => label.startsWith("❖");
function genCode(label: string, existing: Set<string>) {
  let base = label.toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  if (!base) base = "SUB" + Date.now().toString(36).toUpperCase();
  base = base.slice(0, 24);
  let code = base, i = 1;
  while (existing.has(code)) code = `${base}_${i++}`;
  return code.slice(0, 30);
}

// ── Types ─────────────────────────────────────────────────────────────────────

type Cat = { id: number; code: string; label: string; color: string; parent_id: number | null; min_rank: number };

type Notice = {
  id: number; category: string; title: string; body: string;
  author_id: number; author_name: string | null;
  is_pinned: boolean; view_count: number;
  created_at: string;
};

// ── Permissions ───────────────────────────────────────────────────────────────

const WRITE_ROLES = new Set(["SUPER_ADMIN","HR_ADMIN","C_LEVEL","MANAGER","TEAM_LEAD","DIVISION_HEAD","CEO"]);
function useCanWrite() {
  const roles = useAuth(s => s.manifest?.roles ?? s.user?.roles ?? []);
  return roles.some(r => WRITE_ROLES.has(r));
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// Pill 컴포넌트의 Kind("ok"|"warn"|"danger"|"info"|"muted"|"violet")로 매핑 —
// 이전 값(success/warning/default)은 t-pill CSS에 없어 뱃지가 무색으로 렌더됐음.
const PILL_MAP: Record<string,"info"|"danger"|"warn"|"ok"|"muted"> = {
  info:"info", danger:"danger", warning:"warn", success:"ok", default:"muted",
};
const HEX_MAP: Record<string,string> = {
  info:"#3b82f6", danger:"#ef4444", warning:"#f59e0b", success:"#22c55e", default:"#94a3b8",
};

function pillKind(c: string) { return PILL_MAP[c] ?? "info"; }
function hexColor(c: string) { return HEX_MAP[c] ?? HEX_MAP.info; }

function fmtDate(iso: string) {
  const d = new Date(iso);
  return `${String(d.getFullYear()).slice(2)}.${String(d.getMonth()+1).padStart(2,"0")}.${String(d.getDate()).padStart(2,"0")}`;
}

// ── Shared Tab Header ─────────────────────────────────────────────────────────

function BbsTabs({ active }: { active: "notice" | "board" }) {
  const nav = useNavigate();
  const tabs = [
    { key: "notice", label: "📌 공지사항", path: "/notice" },
    { key: "board",  label: "💬 게시판",   path: "/board"  },
  ] as const;
  return (
    <div style={{
      display: "flex", gap: 2, borderBottom: "2px solid var(--border)",
      marginBottom: 14, paddingBottom: 0,
    }}>
      {tabs.map(t => (
        <button key={t.key} onClick={() => nav(t.path)} style={{
          padding: "8px 18px", fontSize: 13, fontWeight: active === t.key ? 700 : 400,
          color: active === t.key ? "var(--p-500, #6366f1)" : "var(--text-3)",
          background: "none", border: "none", cursor: "pointer",
          borderBottom: active === t.key ? "2px solid var(--p-500, #6366f1)" : "2px solid transparent",
          marginBottom: -2, borderRadius: "6px 6px 0 0",
          transition: "all 0.15s",
        }}>{t.label}</button>
      ))}
    </div>
  );
}

// ── Sub-tab row (세부탭 — pill) ─────────────────────────────────────────────────

function subPill(on: boolean): React.CSSProperties {
  return {
    padding: "4px 12px", borderRadius: 20, fontSize: 12, border: "none", cursor: "pointer",
    fontWeight: on ? 700 : 400,
    background: on ? "var(--p-500, #6366f1)" : "var(--panel)",
    color: on ? "#fff" : "var(--text-2)",
  };
}

function SubTabs({ topCat, subTabs, active, onSelect, canManage, onAdd, onRename, onDelete }: {
  topCat: Cat; subTabs: Cat[]; active: string; onSelect: (code: string) => void;
  canManage: boolean; onAdd: () => void; onRename: (c: Cat) => void; onDelete: (c: Cat) => void;
}) {
  const [ctx, setCtx] = useState<{ x: number; y: number; cat: Cat } | null>(null);
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", padding: "10px 0 2px" }}>
      <button onClick={() => onSelect(topCat.code)} style={subPill(active === topCat.code)}>전체</button>
      {subTabs.map(s => (
        <button key={s.code} onClick={() => onSelect(s.code)}
          onContextMenu={e => { if (canManage) { e.preventDefault(); setCtx({ x: e.clientX, y: e.clientY, cat: s }); } }}
          style={subPill(active === s.code)} title={canManage ? "우클릭: 이름변경·삭제" : undefined}>{s.label}</button>
      ))}
      {canManage && (
        <button onClick={onAdd} style={{ ...subPill(false), background: "transparent", border: "1px dashed var(--border)", color: "var(--text-3)" }}>＋ 세부탭</button>
      )}
      {ctx && <ContextMenu x={ctx.x} y={ctx.y} onClose={() => setCtx(null)} items={[
        { label: "이름 변경", icon: "✏️", onClick: () => onRename(ctx.cat) },
        { sep: true },
        { label: "삭제", icon: "🗑️", onClick: () => onDelete(ctx.cat), danger: true },
      ]} />}
    </div>
  );
}

// ── Row ───────────────────────────────────────────────────────────────────────

function NoticeRow({ n, cat, onClick }: { n: Notice; cat: Cat | undefined; onClick: () => void }) {
  const [hov, setHov] = useState(false);
  const accent = hexColor(n.is_pinned ? "danger" : (cat?.color ?? "info"));

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display:"grid",
        gridTemplateColumns:"104px 1fr 90px 76px 52px",
        alignItems:"center",
        padding:"0 20px",
        height:50,
        cursor:"pointer",
        borderBottom:"1px solid var(--border)",
        background: hov
          ? "rgba(99,102,241,0.05)"
          : n.is_pinned
          ? "rgba(239,68,68,0.04)"
          : "transparent",
        transition:"background 0.1s",
      }}
    >
      {/* Category */}
      <div style={{ display:"flex", alignItems:"center", gap:4 }}>
        {n.is_pinned && <span style={{ fontSize:11 }}>📌</span>}
        {cat
          ? <Pill kind={pillKind(cat.color)}>{cat.label}</Pill>
          : <span style={{ fontSize:12, color:"var(--text-3)" }}>{n.category}</span>
        }
      </div>

      {/* Title */}
      <div style={{
        fontSize:14,
        fontWeight: n.is_pinned ? 600 : 400,
        color:"var(--text)",
        overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis",
        paddingLeft:10, marginLeft:4,
        borderLeft:`3px solid ${accent}`,
        paddingRight:12,
      }}>
        {n.title}
      </div>

      {/* Author */}
      <div style={{ fontSize:12, color:"var(--text-3)", overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis" }}>
        {n.author_name ?? `#${n.author_id}`}
      </div>

      {/* Date */}
      <div style={{ fontSize:12, color:"var(--text-3)", textAlign:"center" }}>
        {fmtDate(n.created_at)}
      </div>

      {/* Views */}
      <div style={{ fontSize:12, color:"var(--text-3)", textAlign:"right" }}>
        {n.view_count}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function NoticePage() {
  const [params, setParams] = useSearchParams();
  const navigate  = useNavigate();
  const activeCode = params.get("filter") || "";
  const canWrite  = useCanWrite();
  const canManage = useAuth(s => s.canManageMenu("notice") || s.hasRole("SUPER_ADMIN"));

  const [items,      setItems]      = useState<Notice[] | null>(null);
  const [categories, setCategories] = useState<Cat[]>([]);

  const catMap    = Object.fromEntries(categories.map(c => [c.code, c]));
  const tops      = categories.filter(c => !c.parent_id && !isDivider(c.label));  // 구분선 제외
  const childrenOf = (pid: number) => categories.filter(c => c.parent_id === pid);

  const activeCat = activeCode ? catMap[activeCode] : null;
  const topCat    = activeCat ? (activeCat.parent_id ? categories.find(c => c.id === activeCat.parent_id) ?? null : activeCat) : null;
  const subTabs   = topCat ? childrenOf(topCat.id) : [];

  const select = (code: string) => setParams(p => {
    const n = new URLSearchParams(p);
    if (code) n.set("filter", code); else n.delete("filter");
    return n;
  });

  // ── 세부탭(2차) 관리 — 관리자 ──────────────────────────────────────────────
  const reloadCats = () => {
    api.get<Cat[]>("/notice/categories").then(r => setCategories(r.data)).catch(() => {});
    window.dispatchEvent(new Event("notice-categories-updated"));
  };
  const addSub = async () => {
    if (!topCat) return;
    const label = window.prompt("세부탭 이름")?.trim();
    if (!label) return;
    try {
      await api.post("/notice/categories", { code: genCode(label, new Set(categories.map(c => c.code))), label, color: "info", parent_id: topCat.id, min_rank: 0 });
      reloadCats();
    } catch (e: any) { toast.error("추가 실패", e?.response?.data?.error?.message ?? ""); }
  };
  const renameSub = async (c: Cat) => {
    const label = window.prompt("이름 변경", c.label)?.trim();
    if (!label || label === c.label) return;
    try { await api.patch(`/notice/categories/${c.code}`, { label }); reloadCats(); }
    catch { toast.error("이름 변경 실패"); }
  };
  const deleteSub = async (c: Cat) => {
    if (!window.confirm(`"${c.label}" 세부탭을 삭제하시겠습니까?`)) return;
    try { await api.delete(`/notice/categories/${c.code}`); if (activeCode === c.code && topCat) select(topCat.code); reloadCats(); }
    catch { toast.error("삭제 실패"); }
  };

  useEffect(() => {
    api.get<Cat[]>("/notice/categories").then(r => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setItems(null);
    const q = new URLSearchParams();
    if (activeCode) q.set("category", activeCode);
    q.set("size", "100");
    api.get<{ data: Notice[] }>(`/notice?${q}`)
      .then(r => setItems(r.data.data))
      .catch(() => setItems([]));

    const handler = () => {
      api.get<Cat[]>("/notice/categories").then(r => setCategories(r.data)).catch(() => {});
    };
    window.addEventListener("notice-categories-updated", handler);
    return () => window.removeEventListener("notice-categories-updated", handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCode]);

  const pinned = items?.filter(n => n.is_pinned) ?? [];
  const normal = items?.filter(n => !n.is_pinned) ?? [];

  const showSubTabs = !!topCat && (subTabs.length > 0 || canManage);

  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: "0 28px", width: "100%", boxSizing: "border-box" }}>
      <PageHeader
        title="공지사항"
        subtitle={activeCat ? activeCat.label : "전체 공지"}
        right={
          canWrite
            ? <button className="t-btn primary" style={{ fontSize:13 }} onClick={() => navigate("/notice/new")}>＋ 글쓰기</button>
            : undefined
        }
      />

      {/* 공지/게시판 탭 전환 */}
      <BbsTabs active="notice" />

      {/* 세부탭 (2차) — 카테고리 선택 시 (1차는 좌측 사이드바) */}
      {showSubTabs && topCat && (
        <SubTabs topCat={topCat} subTabs={subTabs} active={activeCode} onSelect={select}
          canManage={canManage} onAdd={addSub} onRename={renameSub} onDelete={deleteSub} />
      )}

      {/* 테이블 */}
      <div style={{
        marginTop: 14,
        background:"var(--surface)", border:"1px solid var(--border)",
        borderRadius:12, overflow:"hidden",
        boxShadow:"0 1px 4px rgba(0,0,0,0.06)",
      }}>
        {/* Column headers */}
        <div style={{
          display:"grid", gridTemplateColumns:"104px 1fr 90px 76px 52px",
          padding:"0 20px", height:36,
          background:"rgba(0,0,0,0.025)",
          borderBottom:"2px solid var(--border)",
          alignItems:"center",
        }}>
          {["분류","제목","작성자","날짜","👁"].map((h, i) => (
            <span key={h} style={{
              fontSize:11, fontWeight:600, color:"var(--text-3)",
              textTransform:"uppercase", letterSpacing:"0.05em",
              textAlign: i >= 3 ? (i === 3 ? "center" : "right") : "left",
              paddingLeft: i === 1 ? 14 : 0,
            }}>
              {h}
            </span>
          ))}
        </div>

        {/* Loading */}
        {items === null && (
          <div style={{ padding:"48px 0", textAlign:"center", color:"var(--text-3)", fontSize:13 }}>
            불러오는 중…
          </div>
        )}

        {/* Empty */}
        {items !== null && items.length === 0 && (
          <div style={{ padding:"64px 0", textAlign:"center", color:"var(--text-3)", fontSize:14 }}>
            공지가 없습니다
          </div>
        )}

        {/* Pinned rows */}
        {pinned.map(n => (
          <NoticeRow key={n.id} n={n} cat={catMap[n.category]} onClick={() => navigate(`/notice/${n.id}`)} />
        ))}

        {/* Divider between pinned and normal */}
        {pinned.length > 0 && normal.length > 0 && (
          <div style={{ borderBottom:"2px dashed var(--border)" }} />
        )}

        {/* Normal rows */}
        {normal.map(n => (
          <NoticeRow key={n.id} n={n} cat={catMap[n.category]} onClick={() => navigate(`/notice/${n.id}`)} />
        ))}
      </div>
    </div>
  );
}
